// Saved Carts account page (Phase 3 list/restore/rename/delete +
// Phase 4 sharing). Renders at /pages/saved-carts.
//
// All worker fetches MUST append the customer's active company_location_id
// from the Liquid-derived config block so Shopify's App Proxy signs it.
// The worker's amendment §16 explicitly forbids reading
// company_location_id from a request body or cookie — we honor that by
// passing it ONLY as a query param on every fetch URL.

(function () {
  'use strict';

  // ── Config ────────────────────────────────────────────────────────────

  function readConfig() {
    var el = document.getElementById('acu-saved-carts-page-config');
    if (!el) return null;
    var locationId = (el.getAttribute('data-customer-company-location-id') || '').trim();
    var companyName = (el.getAttribute('data-customer-company-name') || '').trim();
    return {
      appProxyBase: el.getAttribute('data-app-proxy-base') || '/apps/purchase-history',
      cartPageUrl: el.getAttribute('data-cart-page-url') || '/cart',
      companyLocationId: locationId || null,
      companyName: companyName || null,
      isB2B: !!locationId,
    };
  }

  function withLocation(path, cfg) {
    if (!cfg.companyLocationId) return path;
    var sep = path.indexOf('?') === -1 ? '?' : '&';
    return path + sep + 'company_location_id=' + encodeURIComponent(cfg.companyLocationId);
  }

  function workerFetch(path, opts, cfg) {
    var url = withLocation(cfg.appProxyBase + path, cfg);
    var init = Object.assign({ credentials: 'same-origin' }, opts || {});
    return fetch(url, init);
  }

  function readJSON(resp) {
    return resp.json().then(function (data) {
      if (!resp.ok) {
        var err = new Error((data && data.error) || ('HTTP ' + resp.status));
        err.body = data;
        err.status = resp.status;
        throw err;
      }
      return data;
    });
  }

  // ── State + DOM helpers ───────────────────────────────────────────────

  var state = { carts: [] };
  var rootEl = null;
  var cfg = null;

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  function escapeAttr(s) { return escapeHtml(s); }
  function fmtDate(ms) {
    if (!ms) return '';
    var d = new Date(ms);
    try {
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (_) {
      return d.toDateString();
    }
  }

  // ── Load + render ─────────────────────────────────────────────────────

  function load() {
    rootEl.innerHTML = '<p class="saved-carts__loading">Loading your saved carts…</p>';
    workerFetch('/saved-carts', { method: 'GET' }, cfg)
      .then(readJSON)
      .then(function (data) {
        state.carts = (data && data.carts) || [];
        render();
      })
      .catch(function (err) {
        rootEl.innerHTML =
          '<p class="saved-carts__warnings">Could not load your saved carts: ' +
          escapeHtml(err.message || 'unknown error') +
          '</p>';
      });
  }

  function render() {
    var owned = state.carts.filter(function (c) { return c.scope !== 'shared'; });
    var shared = state.carts.filter(function (c) { return c.scope === 'shared'; });

    if (owned.length === 0 && (!cfg.isB2B || shared.length === 0)) {
      rootEl.innerHTML =
        '<div class="saved-carts__empty">' +
        '  <p>You have not saved any carts yet.</p>' +
        '  <p class="saved-carts__empty-cta">' +
        '    <a class="button button--primary" href="/collections/all">Go shopping</a>' +
        '  </p>' +
        '</div>';
      return;
    }

    var html = '';
    // Owned section
    html += '<div class="saved-carts__section saved-carts__section--owned">';
    html += '  <h2 class="saved-carts__section-title">My saved carts</h2>';
    if (owned.length === 0) {
      html += '  <p class="saved-carts__empty">You have not saved any carts yet.</p>';
    } else {
      html += '  <div class="saved-carts__list">';
      owned.forEach(function (c) { html += renderCard(c); });
      html += '  </div>';
    }
    html += '</div>';

    // Shared section — only when the viewer has a company location
    if (cfg.isB2B) {
      html += '<div class="saved-carts__section saved-carts__section--shared">';
      var title = cfg.companyName
        ? 'Shared by ' + escapeHtml(cfg.companyName)
        : 'Shared by my clinic';
      html += '  <h2 class="saved-carts__section-title">' + title + '</h2>';
      if (shared.length === 0) {
        html += '  <p class="saved-carts__empty">No carts have been shared at this location yet.</p>';
      } else {
        html += '  <div class="saved-carts__list">';
        shared.forEach(function (c) { html += renderCard(c); });
        html += '  </div>';
      }
      html += '</div>';
    }

    rootEl.innerHTML = html;
    bindCardEvents();
  }

  function renderCard(c) {
    var isOwned = c.scope !== 'shared';
    var sharedBadge =
      isOwned && c.is_shared
        ? '<button type="button" class="saved-cart-card__shared-indicator" data-action="toggle-share" data-cart-id="' +
            escapeAttr(c.id) +
          '" title="Click to stop sharing">Shared with clinic</button>'
        : '';
    var ownerLine = !isOwned && c.owner_display_name
      ? '<p class="saved-cart-card__meta">Saved by ' +
          escapeHtml(c.owner_display_name) +
          ' · ' + escapeHtml(fmtDate(c.created_at)) + '</p>'
      : '<p class="saved-cart-card__meta">' +
          escapeHtml(fmtDate(c.created_at)) +
          ' · ' + (c.item_count || 0) + ' item' + ((c.item_count === 1) ? '' : 's') +
        '</p>';

    var preview = '';
    if (Array.isArray(c.preview_titles) && c.preview_titles.length > 0) {
      var titles = c.preview_titles.map(escapeHtml).join(', ');
      var more = c.item_count > c.preview_titles.length
        ? ' <span class="saved-cart-card__preview-more">+' + (c.item_count - c.preview_titles.length) + ' more</span>'
        : '';
      preview = '<p class="saved-cart-card__preview">' + titles + more + '</p>';
    }

    // Action buttons. Shared (non-owned) carts get Restore only — no
    // rename, no delete, no share-toggle, per amendment §14.
    var actions = '';
    actions += '<button type="button" class="saved-cart-card__btn saved-cart-card__btn--primary" data-action="restore" data-cart-id="' + escapeAttr(c.id) + '">Restore</button>';
    actions += '<button type="button" class="saved-cart-card__btn saved-cart-card__btn--secondary" data-action="expand" data-cart-id="' + escapeAttr(c.id) + '">View items</button>';
    if (isOwned) {
      actions += '<button type="button" class="saved-cart-card__btn saved-cart-card__btn--secondary" data-action="rename" data-cart-id="' + escapeAttr(c.id) + '">Rename</button>';
      if (!c.is_shared && cfg.isB2B) {
        actions += '<button type="button" class="saved-cart-card__btn saved-cart-card__btn--secondary" data-action="enable-share" data-cart-id="' + escapeAttr(c.id) + '">Share with clinic</button>';
      }
      actions += '<button type="button" class="saved-cart-card__btn saved-cart-card__btn--danger" data-action="delete" data-cart-id="' + escapeAttr(c.id) + '">Delete</button>';
    }

    return (
      '<div class="saved-cart-card" data-cart-id="' + escapeAttr(c.id) + '" data-scope="' + escapeAttr(c.scope || 'owned') + '">' +
      '  <div class="saved-cart-card__header">' +
      '    <h3 class="saved-cart-card__name" data-name>' + escapeHtml(c.name) + '</h3>' +
           sharedBadge +
      '  </div>' +
           ownerLine +
           preview +
      '  <div class="saved-cart-card__actions">' + actions + '</div>' +
      '  <div class="saved-cart-card__details" hidden data-details></div>' +
      '</div>'
    );
  }

  // ── Event binding ─────────────────────────────────────────────────────

  function bindCardEvents() {
    rootEl.querySelectorAll('[data-action]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var action = btn.getAttribute('data-action');
        var cartId = btn.getAttribute('data-cart-id');
        if (action === 'restore') return startRestore(cartId);
        if (action === 'expand') return toggleExpand(cartId, btn);
        if (action === 'rename') return startRename(cartId);
        if (action === 'delete') return confirmDelete(cartId);
        if (action === 'enable-share') return toggleShare(cartId, true);
        if (action === 'toggle-share') return toggleShare(cartId, false);
      });
    });
  }

  // ── Expand: show full item list with current prices ───────────────────

  function toggleExpand(cartId, btn) {
    var card = rootEl.querySelector('.saved-cart-card[data-cart-id="' + CSS.escape(cartId) + '"]');
    if (!card) return;
    var details = card.querySelector('[data-details]');
    if (!details.hasAttribute('hidden')) {
      details.setAttribute('hidden', '');
      btn.textContent = 'View items';
      return;
    }
    btn.textContent = 'Loading…';
    btn.disabled = true;
    workerFetch('/saved-carts/' + encodeURIComponent(cartId), { method: 'GET' }, cfg)
      .then(readJSON)
      .then(function (data) {
        details.innerHTML = renderDetail(data);
        details.removeAttribute('hidden');
        btn.textContent = 'Hide';
      })
      .catch(function (err) {
        details.innerHTML = '<p class="saved-cart-card__warnings">Could not load details: ' + escapeHtml(err.message) + '</p>';
        details.removeAttribute('hidden');
        btn.textContent = 'Hide';
      })
      .finally(function () { btn.disabled = false; });
  }

  function renderDetail(d) {
    var rows = (d.items || []).map(function (it) {
      var unavail = !it.available || !it.in_current_catalog;
      var cls = 'saved-cart-card__item' + (unavail ? ' saved-cart-card__item--unavailable' : '');
      var qty = '× ' + it.quantity;
      var price = it.current_price ? ('$' + parseFloat(it.current_price).toFixed(2)) : '—';
      var title = it.product_title || it.variant_id;
      if (it.variant_title) title += ' (' + it.variant_title + ')';
      return '<div class="' + cls + '"><span>' + escapeHtml(title) + ' ' + qty + '</span><span>' + escapeHtml(price) + '</span></div>';
    }).join('');
    var warnings = (d.warnings && d.warnings.length)
      ? '<div class="saved-cart-card__warnings"><strong>Note:</strong> ' +
          d.warnings.map(escapeHtml).join('<br>') +
        '</div>'
      : '';
    var totalLine = d.total_estimate
      ? '<div class="saved-cart-card__item"><strong>Estimated total</strong><strong>$' +
          escapeHtml(parseFloat(d.total_estimate).toFixed(2)) +
        '</strong></div>'
      : '';
    return rows + totalLine + warnings;
  }

  // ── Rename: inline contenteditable on the heading ─────────────────────

  function startRename(cartId) {
    var card = rootEl.querySelector('.saved-cart-card[data-cart-id="' + CSS.escape(cartId) + '"]');
    if (!card) return;
    var heading = card.querySelector('[data-name]');
    if (!heading) return;
    var original = heading.textContent;
    heading.setAttribute('contenteditable', 'true');
    heading.focus();
    // Select all text
    var range = document.createRange();
    range.selectNodeContents(heading);
    var sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);

    function finish(commit) {
      heading.removeAttribute('contenteditable');
      heading.blur();
      heading.removeEventListener('keydown', onKey);
      heading.removeEventListener('blur', onBlur);
      var newName = heading.textContent.trim();
      if (!commit || !newName || newName === original) {
        heading.textContent = original;
        return;
      }
      heading.textContent = newName;
      workerFetch('/saved-carts/' + encodeURIComponent(cartId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
      }, cfg)
        .then(readJSON)
        .catch(function (err) {
          alert('Rename failed: ' + (err.message || 'unknown error'));
          heading.textContent = original;
        });
    }

    function onKey(e) {
      if (e.key === 'Enter') { e.preventDefault(); finish(true); }
      if (e.key === 'Escape') { e.preventDefault(); finish(false); }
    }
    function onBlur() { finish(true); }

    heading.addEventListener('keydown', onKey);
    heading.addEventListener('blur', onBlur);
  }

  // ── Delete: confirm via window.confirm, then POST .../delete ──────────

  function confirmDelete(cartId) {
    if (!window.confirm('Delete this saved cart? This cannot be undone.')) return;
    workerFetch('/saved-carts/' + encodeURIComponent(cartId) + '/delete', {
      method: 'POST',
    }, cfg)
      .then(readJSON)
      .then(function () {
        state.carts = state.carts.filter(function (c) { return c.id !== cartId; });
        render();
      })
      .catch(function (err) {
        alert('Could not delete: ' + (err.message || 'unknown error'));
      });
  }

  // ── Share toggle (owner-only) ─────────────────────────────────────────

  function toggleShare(cartId, makeShared) {
    var verb = makeShared ? 'share this cart with your clinic?' : 'stop sharing this cart with your clinic?';
    if (!window.confirm('Are you sure you want to ' + verb)) return;
    workerFetch('/saved-carts/' + encodeURIComponent(cartId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_shared: !!makeShared }),
    }, cfg)
      .then(readJSON)
      .then(function () {
        // Local update + re-render
        state.carts = state.carts.map(function (c) {
          if (c.id === cartId) c.is_shared = !!makeShared;
          return c;
        });
        render();
      })
      .catch(function (err) {
        alert('Could not change sharing: ' + (err.message || 'unknown error'));
      });
  }

  // ── Restore flow ──────────────────────────────────────────────────────
  // Fetch detail to surface any warnings, then ask replace-vs-merge,
  // call /restore on the worker, then post the formatted payload to
  // /cart/add.js (and /cart/clear.js first if in replace mode), then
  // redirect to the cart page.

  function startRestore(cartId) {
    workerFetch('/saved-carts/' + encodeURIComponent(cartId), { method: 'GET' }, cfg)
      .then(readJSON)
      .then(function (detail) {
        chooseRestoreMode(cartId, detail);
      })
      .catch(function (err) {
        alert('Could not load the cart for restore: ' + (err.message || 'unknown error'));
      });
  }

  function chooseRestoreMode(cartId, detail) {
    // If the current cart is empty, skip the replace/merge prompt and
    // go straight to replace.
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var hasItems = cart.items && cart.items.length > 0;
        var mode = 'replace';
        if (hasItems) {
          var msg = 'Your cart currently has items.\n\nOK = REPLACE current cart with the saved cart.\nCancel = MERGE — add saved items to current cart.';
          var replace = window.confirm(msg);
          mode = replace ? 'replace' : 'merge';
        }
        executeRestore(cartId, mode, detail);
      });
  }

  function executeRestore(cartId, mode, detail) {
    var path = '/saved-carts/' + encodeURIComponent(cartId) + '/restore?mode=' + encodeURIComponent(mode);
    workerFetch(path, { method: 'POST' }, cfg)
      .then(readJSON)
      .then(function (data) {
        if (!data.items || data.items.length === 0) {
          alert('No items in this cart are currently available to restore.\n\n' + (data.warnings || []).join('\n'));
          return;
        }
        var prep = mode === 'replace'
          ? fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' })
          : Promise.resolve();

        return prep
          .then(function () {
            return fetch('/cart/add.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: data.items }),
            });
          })
          .then(function (r) {
            if (!r.ok) throw new Error('Add to cart failed (HTTP ' + r.status + ')');
            return r.json();
          })
          .then(function () {
            var note = data.owner_notification
              ? '?restored_by_owner=' + encodeURIComponent(data.owner_notification.owner_display_name || '')
              : '';
            window.location.href = cfg.cartPageUrl + note;
          });
      })
      .catch(function (err) {
        alert('Restore failed: ' + (err.message || 'unknown error'));
      });
  }

  // ── Bootstrap ─────────────────────────────────────────────────────────

  function init() {
    rootEl = document.getElementById('saved-carts-root');
    if (!rootEl) return;
    cfg = readConfig();
    if (!cfg) {
      rootEl.innerHTML = '<p class="saved-carts__warnings">Configuration missing.</p>';
      return;
    }
    load();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
